import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  BranchDto,
  DebitNoteReportRow,
  PurchasePaymentStatus,
  PurchaseReportItem,
  PurchaseReportRow,
  PurchaseReturnReportRow,
  ReportExportRequest,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { Badge } from '@/components/Badge';
import { Modal } from '@/components/Modal';
import { ExportMenu } from '@/components/ExportMenu';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import {
  fetchDebitNotes,
  fetchPurchaseReturns,
  fetchPurchases,
  purchasesReportKeys,
} from '@/features/reports/purchases-report-api';
import { downloadReportExport } from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { buildExportTable, exportSubtitle } from '@/features/reports/export-table';
import type { ExportColumnSpec } from '@/features/reports/export-table';
import type { ReportFilterRow } from '@/features/reports/reportFilters';
import { useReportFilters, type ReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
const qty = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, ''));
const dash = (value: string) => (value.trim() === '' ? '—' : value);
const sum = <T,>(rows: readonly T[], pick: (row: T) => number) => rows.reduce((acc, row) => acc + pick(row), 0);

const STATUS_TONE: Record<PurchasePaymentStatus, 'success' | 'warning' | 'danger'> = {
  Paid: 'success',
  Partial: 'warning',
  Unpaid: 'danger',
};

type TabKey = 'purchases' | 'returns' | 'debit-notes';
const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'purchases', label: 'Purchase list' },
  { key: 'returns', label: 'Purchase returns' },
  { key: 'debit-notes', label: 'Debit notes' },
];

/** The cell that opens a row's detail — the keyboard/screen-reader way in; the row click is the mouse shortcut. */
function OpenButton({ label, onOpen }: { label: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="font-medium text-brand-700 hover:underline"
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      {label}
    </button>
  );
}

/** Per-tab search + Filters popover state, kept local so switching tabs doesn't carry one tab's filter into another. */
function useTabToolbar() {
  const [search, setSearch] = useState('');
  const [filterRows, setFilterRows] = useState<ReportFilterRow[]>([]);
  const [matchAny, setMatchAny] = useState(true);
  return {
    search,
    setSearch,
    filterRows,
    matchAny,
    applyFilters: (rows: ReportFilterRow[], any: boolean) => {
      setFilterRows(rows);
      setMatchAny(any);
    },
  };
}

/**
 * One tab's table as shown — its rows after search/Filters, its visible
 * columns in layout order, its footer totals — titled after the tab.
 */
function purchasesTabTable<T>(
  title: string,
  filters: ReportFilters,
  branches: readonly BranchDto[],
  columns: ReadonlyArray<ExportColumnSpec<T>>,
  visibleKeys: readonly string[],
  rows: readonly T[],
  totals: Record<string, string>,
): ReportExportRequest {
  return buildExportTable({
    title,
    subtitle: exportSubtitle(filters, { branchId: filters.branchId, branches }),
    from: filters.from,
    to: filters.to,
    columns,
    visibleKeys,
    rows,
    totals: { label: 'Total', cells: totals },
  });
}

const percent = (value: number) => `${qty(Math.round(value * 100) / 100)}%`;

/**
 * A record's lines. A pack line reads as the pack it was bought as ("Box of 5":
 * qty in packs, price per pack). `showRates` adds the line Disc % / VAT %
 * columns — purchases carry them, returns don't.
 */
function ItemsTable({
  caption,
  items,
  showRates = false,
}: {
  caption: string;
  items: readonly PurchaseReportItem[];
  showRates?: boolean;
}) {
  const columns: Column<PurchaseReportItem>[] = [
    {
      key: 'productName',
      header: 'Product',
      cell: (row) =>
        row.packLabel ? (
          <>
            {row.productName} <span className="text-slate-500">— {row.packLabel}</span>
          </>
        ) : (
          row.productName
        ),
    },
    { key: 'quantity', header: 'Qty', align: 'right', cell: (row) => qty(row.quantity) },
    { key: 'unitPrice', header: 'Unit price', align: 'right', cell: (row) => money(row.unitPrice) },
    ...(showRates
      ? ([
          { key: 'discountPercent', header: 'Disc %', align: 'right', cell: (row) => percent(row.discountPercent) },
          { key: 'taxPercent', header: 'VAT %', align: 'right', cell: (row) => percent(row.taxPercent) },
        ] satisfies Column<PurchaseReportItem>[])
      : []),
    { key: 'total', header: 'Total', align: 'right', cell: (row) => money(row.total) },
  ];
  if (items.length === 0) return <p className="text-sm text-slate-500">No lines synced for this record.</p>;
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <DataTable
        caption={caption}
        columns={columns}
        rows={items}
        rowKey={(row, index) => `${row.productName}-${index}`}
        totals={{ label: 'Total', cells: { total: money(sum(items, (row) => row.total)) } }}
      />
    </div>
  );
}

function DetailGrid({ entries }: { entries: ReadonlyArray<[string, string]> }) {
  return (
    <dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {entries.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3">
          <dt className="text-slate-500">{label}</dt>
          <dd className="text-right font-medium text-slate-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ─── Purchase list ──────────────────────────────────────────────────────────

const PURCHASE_FILTER_FIELDS = ['Supplier', 'Payment method', 'Status', 'Invoice #'] as const;
const PURCHASE_TOGGLEABLE = [
  'date', 'supplierName', 'invoiceNumber', 'paymentMethod', 'subtotal', 'discountAmount',
  'taxAmount', 'total', 'paid', 'balance', 'status',
] as const;
const PURCHASE_LABELS: Record<string, string> = {
  date: 'Date',
  supplierName: 'Supplier',
  invoiceNumber: 'Invoice #',
  paymentMethod: 'Payment',
  subtotal: 'Subtotal',
  discountAmount: 'Discount',
  taxAmount: 'VAT',
  total: 'Total',
  paid: 'Paid',
  balance: 'Balance',
  status: 'Status',
};

const PURCHASE_EXPORT: ExportColumnSpec<PurchaseReportRow>[] = [
  { key: 'purchaseNumber', header: 'Purchase #', text: (row) => row.purchaseNumber },
  { key: 'date', header: 'Date', text: (row) => row.date },
  { key: 'supplierName', header: 'Supplier', width: 2, text: (row) => dash(row.supplierName) },
  { key: 'invoiceNumber', header: 'Invoice #', text: (row) => dash(row.invoiceNumber) },
  { key: 'paymentMethod', header: 'Payment', text: (row) => row.paymentMethod },
  { key: 'subtotal', header: 'Subtotal', align: 'right', money: true, text: (row) => money(row.subtotal) },
  { key: 'discountAmount', header: 'Discount', align: 'right', money: true, text: (row) => money(row.discountAmount) },
  { key: 'taxAmount', header: 'VAT', align: 'right', money: true, text: (row) => money(row.taxAmount) },
  { key: 'total', header: 'Total', align: 'right', money: true, text: (row) => money(row.total) },
  { key: 'paid', header: 'Paid', align: 'right', money: true, text: (row) => money(row.paid) },
  { key: 'balance', header: 'Balance', align: 'right', money: true, text: (row) => money(row.balance) },
  { key: 'status', header: 'Status', text: (row) => row.status },
];

function PurchasesTab({ filters }: { filters: ReportFilters }) {
  const { profile } = usePortal();
  const toolbar = useTabToolbar();
  const layout = useReportLayout('purchases-list', PURCHASE_TOGGLEABLE);
  const [open, setOpen] = useState<PurchaseReportRow | null>(null);
  const query = useQuery({
    queryKey: purchasesReportKeys.purchases(filters),
    queryFn: ({ signal }) => fetchPurchases(filters, signal),
  });

  const columns = applyReportLayout<PurchaseReportRow>(
    [
      {
        key: 'purchaseNumber',
        header: 'Purchase #',
        pinned: true,
        cell: (row) => <OpenButton label={row.purchaseNumber} onOpen={() => setOpen(row)} />,
      },
      { key: 'date', header: 'Date', cell: (row) => row.date },
      { key: 'supplierName', header: 'Supplier', cell: (row) => dash(row.supplierName) },
      { key: 'invoiceNumber', header: 'Invoice #', cell: (row) => dash(row.invoiceNumber) },
      { key: 'paymentMethod', header: 'Payment', cell: (row) => row.paymentMethod },
      { key: 'subtotal', header: 'Subtotal', align: 'right', cell: (row) => money(row.subtotal) },
      { key: 'discountAmount', header: 'Discount', align: 'right', cell: (row) => money(row.discountAmount) },
      { key: 'taxAmount', header: 'VAT', align: 'right', cell: (row) => money(row.taxAmount) },
      { key: 'total', header: 'Total', align: 'right', cell: (row) => money(row.total) },
      { key: 'paid', header: 'Paid', align: 'right', cell: (row) => money(row.paid) },
      { key: 'balance', header: 'Balance', align: 'right', cell: (row) => money(row.balance) },
      {
        key: 'status',
        header: 'Status',
        cell: (row) => (
          <Badge tone={STATUS_TONE[row.status]} dot>
            {row.status}
          </Badge>
        ),
      },
    ],
    layout,
  );

  const rows = useMemo(
    () =>
      applyClientFilters(query.data ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Supplier'
            ? row.supplierName
            : field === 'Payment method'
              ? row.paymentMethod
              : field === 'Status'
                ? row.status
                : field === 'Invoice #'
                  ? row.invoiceNumber
                  : undefined,
        searchValues: (row) => [row.purchaseNumber, row.supplierName, row.invoiceNumber],
      }),
    [query.data, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );
  const totals = {
    subtotal: money(sum(rows, (row) => row.subtotal)),
    discountAmount: money(sum(rows, (row) => row.discountAmount)),
    taxAmount: money(sum(rows, (row) => row.taxAmount)),
    total: money(sum(rows, (row) => row.total)),
    paid: money(sum(rows, (row) => row.paid)),
    balance: money(sum(rows, (row) => row.balance)),
  };

  const onExport = (format: ExportFormat) =>
    downloadReportExport(
      purchasesTabTable(
        'Purchases',
        filters,
        profile?.branches ?? [],
        PURCHASE_EXPORT,
        columns.map((column) => column.key),
        rows,
        totals,
      ),
      format,
    );

  return (
    <>
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search purchase #, supplier or invoice #"
        />
        <ReportFiltersButton
          fields={PURCHASE_FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Status'
              ? 'Paid / Partial / Unpaid'
              : field === 'Payment method'
                ? 'Cash / Card / Credit'
                : field === 'Invoice #'
                  ? 'invoice #'
                  : 'supplier'
          }
        />
        <ReportColumnsButton layout={layout} labels={PURCHASE_LABELS} />
        {rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={() => rows.length === 0}
        emptyTitle={toolbar.search || toolbar.filterRows.length ? 'No purchases match' : 'No purchases in this window'}
        skeleton={<TableSkeleton columns={12} />}
      >
        {() => (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <DataTable
              caption="Purchases"
              layout={layout}
              columns={columns}
              rows={rows}
              rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
              onRowClick={setOpen}
              totals={{ label: 'Total', cells: totals }}
            />
          </div>
        )}
      </AsyncSection>
      <Modal
        open={open !== null}
        title={open ? `Purchase ${open.purchaseNumber}` : 'Purchase'}
        description={open ? `${dash(open.supplierName)} · ${open.date}` : undefined}
        onClose={() => setOpen(null)}
        size="lg"
      >
        {open ? (
          <>
            <DetailGrid
              entries={[
                ['Invoice #', dash(open.invoiceNumber)],
                ['Payment', open.paymentMethod],
                ['Subtotal', money(open.subtotal)],
                ['Discount', money(open.discountAmount)],
                ['VAT', money(open.taxAmount)],
                ['Total', money(open.total)],
                ...(open.paymentMethod === 'Credit'
                  ? ([
                      ['Paid so far', money(open.paid)],
                      ['Balance', money(open.balance)],
                    ] as Array<[string, string]>)
                  : []),
                ['Status', open.status],
              ]}
            />
            <ItemsTable caption={`Items on purchase ${open.purchaseNumber}`} items={open.items} showRates />
          </>
        ) : null}
      </Modal>
    </>
  );
}

// ─── Purchase returns ───────────────────────────────────────────────────────

const RETURN_FILTER_FIELDS = ['Supplier', 'Purchase #', 'Reason'] as const;
const RETURN_TOGGLEABLE = ['date', 'supplierName', 'purchaseNumber', 'reason', 'items', 'refundAmount'] as const;
const RETURN_LABELS: Record<string, string> = {
  date: 'Date',
  supplierName: 'Supplier',
  purchaseNumber: 'Purchase #',
  reason: 'Reason',
  items: 'Items',
  refundAmount: 'Refund',
};

const RETURN_EXPORT: ExportColumnSpec<PurchaseReturnReportRow>[] = [
  { key: 'returnNumber', header: 'Return #', text: (row) => row.returnNumber },
  { key: 'date', header: 'Date', text: (row) => row.date },
  { key: 'supplierName', header: 'Supplier', width: 2, text: (row) => dash(row.supplierName) },
  { key: 'purchaseNumber', header: 'Purchase #', text: (row) => dash(row.purchaseNumber) },
  { key: 'reason', header: 'Reason', width: 2, text: (row) => dash(row.reason) },
  { key: 'items', header: 'Items', align: 'right', numeric: true, text: (row) => String(row.items.length) },
  { key: 'refundAmount', header: 'Refund', align: 'right', money: true, text: (row) => money(row.refundAmount) },
];

function ReturnsTab({ filters }: { filters: ReportFilters }) {
  const { profile } = usePortal();
  const toolbar = useTabToolbar();
  const layout = useReportLayout('purchase-returns', RETURN_TOGGLEABLE);
  const [open, setOpen] = useState<PurchaseReturnReportRow | null>(null);
  const query = useQuery({
    queryKey: purchasesReportKeys.purchaseReturns(filters),
    queryFn: ({ signal }) => fetchPurchaseReturns(filters, signal),
  });

  const columns = applyReportLayout<PurchaseReturnReportRow>(
    [
      {
        key: 'returnNumber',
        header: 'Return #',
        pinned: true,
        cell: (row) => <OpenButton label={row.returnNumber} onOpen={() => setOpen(row)} />,
      },
      { key: 'date', header: 'Date', cell: (row) => row.date },
      { key: 'supplierName', header: 'Supplier', cell: (row) => dash(row.supplierName) },
      { key: 'purchaseNumber', header: 'Purchase #', cell: (row) => dash(row.purchaseNumber) },
      { key: 'reason', header: 'Reason', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.reason) },
      { key: 'items', header: 'Items', align: 'right', cell: (row) => String(row.items.length) },
      { key: 'refundAmount', header: 'Refund', align: 'right', cell: (row) => money(row.refundAmount) },
    ],
    layout,
  );

  const rows = useMemo(
    () =>
      applyClientFilters(query.data ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Supplier'
            ? row.supplierName
            : field === 'Purchase #'
              ? row.purchaseNumber
              : field === 'Reason'
                ? row.reason
                : undefined,
        searchValues: (row) => [row.returnNumber, row.supplierName, row.purchaseNumber, row.reason],
      }),
    [query.data, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );

  const onExport = (format: ExportFormat) =>
    downloadReportExport(
      purchasesTabTable(
        'Purchase returns',
        filters,
        profile?.branches ?? [],
        RETURN_EXPORT,
        columns.map((column) => column.key),
        rows,
        { refundAmount: money(sum(rows, (row) => row.refundAmount)) },
      ),
      format,
    );

  return (
    <>
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search return #, supplier, purchase # or reason"
        />
        <ReportFiltersButton
          fields={RETURN_FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) => (field === 'Purchase #' ? 'purchase #' : field === 'Reason' ? 'reason' : 'supplier')}
        />
        <ReportColumnsButton layout={layout} labels={RETURN_LABELS} />
        {rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={() => rows.length === 0}
        emptyTitle={
          toolbar.search || toolbar.filterRows.length ? 'No purchase returns match' : 'No purchase returns in this window'
        }
        skeleton={<TableSkeleton columns={7} />}
      >
        {() => (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <DataTable
              caption="Purchase returns"
              layout={layout}
              columns={columns}
              rows={rows}
              rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
              onRowClick={setOpen}
              totals={{ label: 'Total', cells: { refundAmount: money(sum(rows, (row) => row.refundAmount)) } }}
            />
          </div>
        )}
      </AsyncSection>
      <Modal
        open={open !== null}
        title={open ? `Purchase return ${open.returnNumber}` : 'Purchase return'}
        description={open ? `${dash(open.supplierName)} · ${open.date}` : undefined}
        onClose={() => setOpen(null)}
        size="lg"
      >
        {open ? (
          <>
            <DetailGrid
              entries={[
                ['Purchase #', dash(open.purchaseNumber)],
                ['Refund', money(open.refundAmount)],
                ['Reason', dash(open.reason)],
              ]}
            />
            <ItemsTable caption={`Items on purchase return ${open.returnNumber}`} items={open.items} />
          </>
        ) : null}
      </Modal>
    </>
  );
}

// ─── Debit notes ────────────────────────────────────────────────────────────

const NOTE_FILTER_FIELDS = ['Supplier', 'Purchase #', 'Method', 'Reason', 'By'] as const;
// Amount / Off payable / Method start hidden, as on the POS's own Debit Notes page.
const NOTE_TOGGLEABLE = [
  'date', 'purchaseNumber', 'supplierName', 'netAmount', 'taxAmount', 'amount', 'againstPayable',
  'refundAmount', 'refundMethod', 'reason', 'createdBy',
] as const;
const NOTE_DEFAULT_HIDDEN = ['amount', 'againstPayable', 'refundMethod'] as const;
const NOTE_LABELS: Record<string, string> = {
  date: 'Date',
  purchaseNumber: 'Purchase #',
  supplierName: 'Supplier',
  netAmount: 'Net',
  taxAmount: 'VAT',
  amount: 'Amount',
  againstPayable: 'Off payable',
  refundAmount: 'Received',
  refundMethod: 'Method',
  reason: 'Reason',
  createdBy: 'By',
};

const NOTE_EXPORT: ExportColumnSpec<DebitNoteReportRow>[] = [
  { key: 'noteNumber', header: 'Note #', text: (row) => row.noteNumber },
  { key: 'date', header: 'Date', text: (row) => row.date },
  { key: 'purchaseNumber', header: 'Purchase #', text: (row) => dash(row.purchaseNumber) },
  { key: 'supplierName', header: 'Supplier', width: 2, text: (row) => dash(row.supplierName) },
  { key: 'netAmount', header: 'Net', align: 'right', money: true, text: (row) => money(row.netAmount) },
  { key: 'taxAmount', header: 'VAT', align: 'right', money: true, text: (row) => money(row.taxAmount) },
  { key: 'amount', header: 'Amount', align: 'right', money: true, text: (row) => money(row.amount) },
  {
    key: 'againstPayable',
    header: 'Off payable',
    align: 'right',
    money: true,
    text: (row) => (row.againstPayable > 0.005 ? money(row.againstPayable) : '—'),
  },
  {
    key: 'refundAmount',
    header: 'Received',
    align: 'right',
    money: true,
    text: (row) => (row.refundAmount > 0.005 ? money(row.refundAmount) : '—'),
  },
  { key: 'refundMethod', header: 'Method', text: (row) => dash(row.refundMethod) },
  { key: 'reason', header: 'Reason', width: 2, text: (row) => dash(row.reason) },
  { key: 'createdBy', header: 'By', text: (row) => dash(row.createdBy) },
];

function DebitNotesTab({ filters }: { filters: ReportFilters }) {
  const { profile } = usePortal();
  const toolbar = useTabToolbar();
  const layout = useReportLayout('debit-notes', NOTE_TOGGLEABLE, NOTE_DEFAULT_HIDDEN);
  const [open, setOpen] = useState<DebitNoteReportRow | null>(null);
  const query = useQuery({
    queryKey: purchasesReportKeys.debitNotes(filters),
    queryFn: ({ signal }) => fetchDebitNotes(filters, signal),
  });

  const columns = applyReportLayout<DebitNoteReportRow>(
    [
      {
        key: 'noteNumber',
        header: 'Note #',
        pinned: true,
        cell: (row) => <OpenButton label={row.noteNumber} onOpen={() => setOpen(row)} />,
      },
      { key: 'date', header: 'Date', cell: (row) => row.date },
      { key: 'purchaseNumber', header: 'Purchase #', cell: (row) => dash(row.purchaseNumber) },
      { key: 'supplierName', header: 'Supplier', cell: (row) => dash(row.supplierName) },
      { key: 'netAmount', header: 'Net', align: 'right', cell: (row) => money(row.netAmount) },
      { key: 'taxAmount', header: 'VAT', align: 'right', cell: (row) => money(row.taxAmount) },
      { key: 'amount', header: 'Amount', align: 'right', cell: (row) => money(row.amount) },
      {
        key: 'againstPayable',
        header: 'Off payable',
        align: 'right',
        cell: (row) => (row.againstPayable > 0.005 ? money(row.againstPayable) : '—'),
      },
      {
        key: 'refundAmount',
        header: 'Received',
        align: 'right',
        cell: (row) => (row.refundAmount > 0.005 ? money(row.refundAmount) : '—'),
      },
      { key: 'refundMethod', header: 'Method', cell: (row) => dash(row.refundMethod) },
      { key: 'reason', header: 'Reason', visibility: 'hidden sm:table-cell', cell: (row) => dash(row.reason) },
      { key: 'createdBy', header: 'By', cell: (row) => dash(row.createdBy) },
    ],
    layout,
  );

  const rows = useMemo(
    () =>
      applyClientFilters(query.data ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Supplier'
            ? row.supplierName
            : field === 'Purchase #'
              ? row.purchaseNumber
              : field === 'Method'
                ? row.refundMethod
                : field === 'Reason'
                  ? row.reason
                  : field === 'By'
                    ? row.createdBy
                    : undefined,
        searchValues: (row) => [row.noteNumber, row.supplierName, row.purchaseNumber, row.reason],
      }),
    [query.data, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );
  const totals = {
    netAmount: money(sum(rows, (row) => row.netAmount)),
    taxAmount: money(sum(rows, (row) => row.taxAmount)),
    amount: money(sum(rows, (row) => row.amount)),
    againstPayable: money(sum(rows, (row) => row.againstPayable)),
    refundAmount: money(sum(rows, (row) => row.refundAmount)),
  };

  const onExport = (format: ExportFormat) =>
    downloadReportExport(
      purchasesTabTable(
        'Debit notes',
        filters,
        profile?.branches ?? [],
        NOTE_EXPORT,
        columns.map((column) => column.key),
        rows,
        totals,
      ),
      format,
    );

  return (
    <>
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search note #, supplier, purchase # or reason"
        />
        <ReportFiltersButton
          fields={NOTE_FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Purchase #'
              ? 'purchase #'
              : field === 'Method'
                ? 'Cash / Card'
                : field === 'Reason'
                  ? 'reason'
                  : field === 'By'
                    ? 'staff'
                    : 'supplier'
          }
        />
        <ReportColumnsButton layout={layout} labels={NOTE_LABELS} />
        {rows.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={() => rows.length === 0}
        emptyTitle={toolbar.search || toolbar.filterRows.length ? 'No debit notes match' : 'No debit notes in this window'}
        skeleton={<TableSkeleton columns={12} />}
      >
        {() => (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <DataTable
              caption="Debit notes"
              layout={layout}
              columns={columns}
              rows={rows}
              rowKey={(row) => `${row.deviceId}-${row.sourceId}`}
              onRowClick={setOpen}
              totals={{ label: 'Total', cells: totals }}
            />
          </div>
        )}
      </AsyncSection>
      <Modal
        open={open !== null}
        title={open ? `Debit note ${open.noteNumber}` : 'Debit note'}
        description={open ? `${dash(open.supplierName)} · ${open.date}` : undefined}
        onClose={() => setOpen(null)}
      >
        {open ? (
          <DetailGrid
            entries={[
              ['Purchase #', dash(open.purchaseNumber)],
              ['Net', money(open.netAmount)],
              ['VAT', money(open.taxAmount)],
              ['Amount', money(open.amount)],
              ['Off payable', money(open.againstPayable)],
              ['Received', money(open.refundAmount)],
              ['Method', dash(open.refundMethod)],
              ['Reason', dash(open.reason)],
              ['Note', dash(open.note)],
              ['By', dash(open.createdBy)],
            ]}
          />
        ) : null}
      </Modal>
    </>
  );
}

/**
 * The POS's Purchases screens in one page: the purchase list, purchase
 * returns history and debit notes, each a tab over the same branch + date
 * range. Every endpoint returns the whole window with its lines, so a row's
 * detail opens instantly and search/filters run client-side.
 */
export function PurchasesReportPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [tab, setTab] = useState<TabKey>('purchases');

  return (
    <div>
      <PageHeader
        title="Purchases"
        description="Supplier purchases, purchase returns and debit notes, dated by the day they were recorded."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200">
        <div role="tablist" aria-label="Purchases report" className="-mb-px flex gap-1">
          {TABS.map((item) => {
            const selected = item.key === tab;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                id={`purchases-tab-${item.key}`}
                aria-selected={selected}
                aria-controls={`purchases-panel-${item.key}`}
                className={`border-b-2 px-3 py-2 text-sm font-medium ${
                  selected
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        <div className="pb-2">
          <ReportPeriodButton
            from={filters.from}
            to={filters.to}
            onChange={(range) => setFilters({ from: range.from, to: range.to })}
          />
        </div>
      </div>
      <div role="tabpanel" id={`purchases-panel-${tab}`} aria-labelledby={`purchases-tab-${tab}`}>
        {tab === 'purchases' ? <PurchasesTab filters={filters} /> : null}
        {tab === 'returns' ? <ReturnsTab filters={filters} /> : null}
        {tab === 'debit-notes' ? <DebitNotesTab filters={filters} /> : null}
      </div>
    </div>
  );
}
